/** @jsxImportSource @emotion/core */
import { css } from '@emotion/core';
import React from 'react';
import { useParams } from 'react-router-dom';
import { MdBusiness, MdDateRange } from 'react-icons/md';

import { ResponsiveWrapper, Wrapper } from 'components/styled/Wrapper';
import TitleText from 'components/styled/TitleText';
import Text from 'components/styled/Text';
import Link from 'components/styled/Link';
import { Box, Flex } from 'components/styled';
import MainSectionItem from 'components/profile/MainSectionItem';
import Avatar from 'components/styled/Avatar';
import ProfileRow from 'components/profile/ProfileRow';
import { SkillBox, SkillGroup, SkillTitle } from 'components/profile/Skill';
import ErrorScreen from 'components/error/ErrorScreen';

import { Loader } from 'modules/loader';

import { PiggyIcon } from 'assets';
import { getDateString } from 'utils';

import { ReactComponent as LocationPrefIcon } from 'assets/locationPref.svg';

import { getFile } from 'api/fileApi';
import { useLimitedOfferDto } from 'api/offerApi';

import { getMunicipality } from 'utils/address';
import { EducationTypeToText, EmploymentTypeText } from 'utils/enumMaps';

import LoginOptions from '../loginRedirect/LoginOptions';

import { OfferLimitedDto } from 'typing/endpoints';

const PublicOfferDetail = () => {
	const { id } = useParams<{ id: string }>();
	const dto = useLimitedOfferDto(id);
	if (dto.isLoading) {
		return <Loader />;
	}
	if (!dto.isSuccess || dto.isError) {
		return <ErrorScreen error={dto.error} refetch={dto.refetch} />;
	}
	const {
		title,
		customTitle,
		logoOfCompany,
		companyName,
		addresses,
		showSalary,
		salary,
		validTo,
		employmentTypes,
		suitableForEducationTypes,
		isRemoteWorkPossible,
		isAcceptingForeignStudents,
		suitableForSPP,
	} = dto.data as OfferLimitedDto;
	return (
		<Box /* bg="secondary" */>
			{/* <FullBackground imgSrc={bgImg} /> */}
			<ResponsiveWrapper mt={4}>
				<Box
					position="absolute"
					top={0}
					left={0}
					width="100%"
					height="100%"
					zIndex={1}
					css={() => css`
						background: linear-gradient(0deg, #f7f7f7, transparent);
						//background: linear-gradient(0deg, white, transparent);
						// background: linear-gradient(0deg, #d2d2d2, transparent);
					`}
				></Box>
				<Box bg="lightGrey">
					<Box width="100%" position="relative">
						<Box width="100%" px={0}>
							<TitleText fontSize={[20, 24, 26, 32]} my={0} p={3}>
								{title?.name ?? customTitle ?? '<<Nepojmenovaná pozice>>'}
							</TitleText>
						</Box>
					</Box>

					<Flex
						justifyContent="space-between"
						alignItems={['center', 'center', 'center', 'flex-start']}
						p={[3, 3, 3]}
						my={1}
						backgroundColor="white"
						flexDirection={['column', 'column', 'column', 'row']}
						fontSize={['md', 'md', 'md', 'lg']}
						height="100%"
						minHeight="100%"
					>
						{logoOfCompany && (
							<Flex
								flexGrow={0}
								flexBasis={['0', '0', '0', '190px']}
								mb={[3, 3, 3, 0]}
								pr={[0, 0, 4]}
								mt={2}
								justifyContent="center"
							>
								<Avatar
									photo={getFile(logoOfCompany)}
									size={[50, 70, 100, 100]}
									flexShrink={0}
								/>
							</Flex>
						)}

						<MainSectionItem title="Společnost" icon={MdBusiness}>
							<Link
								to={``}
								maxWidth={['unset', 'unset', 'unset', '120px']}
								display="block"
							>
								{companyName}
							</Link>
						</MainSectionItem>

						<MainSectionItem title="Místo výkonu práce" icon={LocationPrefIcon}>
							{addresses?.[0] ? (
								<Text
									key={addresses[0].id}
									as="span"
									maxWidth={['unset', 'unset', 'unset', '120px']}
									display="block"
								>
									{getMunicipality(addresses[0])}
								</Text>
							) : (
								<>{isRemoteWorkPossible ? 'Práce na dálku' : ''}</>
							)}
						</MainSectionItem>

						<MainSectionItem title="Nabízený plat" icon={PiggyIcon}>
							<Text
								as="span"
								maxWidth={['unset', 'unset', 'unset', '120px']}
								display="block"
							>
								{showSalary && salary
									? `${salary.minSalary.toLocaleString(
											'cs-CZ',
									  )} Kč - ${salary.maxSalary.toLocaleString('cs-CZ')} Kč${
											salary?.salaryType === 'HOURLY' ? '/ h' : ''
									  }`
									: 'Na dotaz'}
							</Text>
						</MainSectionItem>
						{validTo && (
							<MainSectionItem title="Platnost" icon={MdDateRange}>
								<Text
									as="span"
									maxWidth={['unset', 'unset', 'unset', '120px']}
									display="block"
								>
									Aktivní do {getDateString(validTo)}
								</Text>
							</MainSectionItem>
						)}
					</Flex>
				</Box>
				<Flex
					justifyContent="space-between"
					alignItems="center"
					p={4}
					my={2}
					backgroundColor="white"
					flexDirection="column"
				>
					{isAcceptingForeignStudents && (
						<Flex
							alignSelf="end"
							justifyContent="center"
							flexDirection="column"
							height={['unset', 0]}
						>
							<Text>
								<Text fontWeight="bold" as="span" color="primary" fontSize="lg">
									{'+ '}
								</Text>
								<Text fontWeight="bold" color="text" as="span" fontSize="lg">
									Foreign{' '}
								</Text>
								<Text color="text" as="span" fontWeight="normal">
									employees welcome
								</Text>
							</Text>
						</Flex>
					)}
					<ProfileRow title="Místa výkonu práce">
						{addresses && addresses.length > 0 && (
							<SkillGroup>
								{addresses.slice(0).map(address => (
									<SkillBox key={`address-profile-row-${address.id}`}>
										<SkillTitle>{getMunicipality(address)}</SkillTitle>
									</SkillBox>
								))}
							</SkillGroup>
						)}

						<>
							{isRemoteWorkPossible && (
								<Text>
									<Text as="span" color="primary">
										-
									</Text>{' '}
									Možnost pracovat na dálku
								</Text>
							)}
						</>
					</ProfileRow>
					{suitableForSPP && (
						<ProfileRow title="Vhodné pro SSP">
							<SkillGroup>
								<SkillBox>
									<SkillTitle>
										Vhodné pro studenty se specifickými potřebami
									</SkillTitle>
								</SkillBox>
							</SkillGroup>
						</ProfileRow>
					)}
					{employmentTypes && employmentTypes.length > 0 && (
						<ProfileRow title="Typ spolupráce">
							<SkillGroup>
								{employmentTypes.map(e => (
									<SkillBox key={`empl-type-${e}`}>
										<SkillTitle>{EmploymentTypeText[e]}</SkillTitle>
									</SkillBox>
								))}
							</SkillGroup>
						</ProfileRow>
					)}
					{suitableForEducationTypes && suitableForEducationTypes.length > 0 && (
						<ProfileRow title="Vhodné pro">
							<SkillGroup>
								{suitableForEducationTypes.map(e => (
									<SkillBox key={`suitableForEducationTypes-${e}`}>
										<SkillTitle>{EducationTypeToText[e]}</SkillTitle>
									</SkillBox>
								))}
							</SkillGroup>
						</ProfileRow>
					)}
				</Flex>
			</ResponsiveWrapper>

			<Wrapper
				textAlign="center"
				zIndex={2}
				backgroundColor="#d2d2d2"
				color="black"
				position="relative"
				css={css`
					box-shadow: 0px 20px 12px 20px #0000002b;
					text-shadow: 1px 1px #ffffff61;
				`}
			>
				<Text
					fontSize="xl"
					fontWeight="bold"
					mt={4}
					mb={4}
					as="h2"
					color="black"
				>
					Pro přístup k téhle stránce musíte být přihlášen
				</Text>
				<Text>Prosím přihlašte se jednou z možností níže.</Text>

				<Flex mb={0} flexGrow={1} alignItems="flex-end">
					<LoginOptions
						externalState={{ redirect: `/offer/${id}` }}
						offerVariant
					/>
				</Flex>
			</Wrapper>
		</Box>
	);
};

export default PublicOfferDetail;
